import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Pagination,
} from '@mui/material';

const EloRanking = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [teamsData, setTeamsData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [levels, setLevels] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState(() => {
	  const regionFromUrl = searchParams.get('region');
	  return (regionFromUrl && !isNaN(Number(regionFromUrl))) ? regionFromUrl : 'all';
	});

  const PAGE_SIZE = 100;

  useEffect(() => {
  const regionFromUrl = searchParams.get('region');
  if (regionFromUrl && !isNaN(Number(regionFromUrl))) {
    if (selectedRegion !== regionFromUrl) {
      setSelectedRegion(regionFromUrl);
      setCurrentPage(1);
    }
  } else if (!regionFromUrl && selectedRegion !== 'all') {
    setSelectedRegion('all');
    setCurrentPage(1);
  }
}, [searchParams]);

  // Poziomy 1–10
  useEffect(() => {
    setLevels([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  }, []);

  // Pobieranie listy regionów z nowego endpointu
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/elo-ranking/regions`)
      .then(res => {
        const sorted = res.data.sort((a, b) => a.name.localeCompare(b.name));
        setRegions(sorted);
      })
      .catch(err => {
        console.warn('Nie udało się pobrać listy regionów', err);
        setRegions([]);
      });
  }, []);

  // Główny fetch danych
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: (currentPage - 1).toString(),
        size: PAGE_SIZE.toString(),
      });
      if (selectedLevel !== 'all') params.append('level', selectedLevel);
      if (selectedRegion !== 'all') params.append('region', selectedRegion);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/elo-ranking?${params}`
      );
      const res = response.data;

      setTeamsData(res.content || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      setError('Błąd podczas pobierania danych rankingu ELO.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, selectedLevel, selectedRegion]);

  // Handlery
  const handleLevelChange = (e) => {
    setSelectedLevel(e.target.value);
    setCurrentPage(1);
  };

  const handleRegionChange = (e) => {
    const val = e.target.value;
    setSelectedRegion(val);
    setCurrentPage(1);

    if (val === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ region: val });
    }
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Nawigacja po kliknięciu
  const goToTeam = (teamId) => navigate(`/team/${teamId}`);
  const goToLeague = (competitionId) => {
    if (competitionId) navigate(`/liga/${competitionId}`);
  };

  if (loading && teamsData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: '1px', md: '5px' } }}>
      <Typography variant="h5" sx={{ mb: 3, textAlign: { xs: 'center', sm: 'center' } }}>
        Ranking ELO
      </Typography>

      {/* Filtry */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="level-select-label">Poziom rozgrywek</InputLabel>
          <Select
            labelId="level-select-label"
            value={selectedLevel}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.9rem' },
              height: { xs: '35px', sm: '52px' },
              padding: '0 8px',
              '& .MuiSelect-select': { padding: '4px', lineHeight: '1.2' },
            }}
            onChange={handleLevelChange}
          >
            <MenuItem value="all" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, padding: '4px 8px', minHeight: '24px' }}>
              Wszystkie
            </MenuItem>
            {levels.map((level) => (
              <MenuItem
                key={level}
                value={level}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, padding: '4px 8px', minHeight: '24px' }}
              >
                Poziom {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="region-select-label">Region</InputLabel>
          <Select
            labelId="region-select-label"
            value={selectedRegion}
            onChange={handleRegionChange}
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.9rem' },
              height: { xs: '35px', sm: '52px' },
              padding: '0 8px',
              '& .MuiSelect-select': { padding: '4px', lineHeight: '1.2' },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, padding: '4px 8px', minHeight: '24px' }}>
              Wszystkie
            </MenuItem>
            {regions.map((region) => (
              <MenuItem
                key={region.id}
                value={region.id}
                sx={{ fontSize: { xs: '0.7rem', sm: '0.9rem' }, padding: '4px 8px', minHeight: '24px' }}
              >
                {region.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Paginacja na górze */}
{totalPages > 1 && (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      my: 2,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }}
  >
    <Pagination
      count={totalPages}
      page={currentPage}
      onChange={handlePageChange}
      size="medium"
      siblingCount={1}
      boundaryCount={1}
      showFirstButton
      showLastButton
      color="primary"
      sx={{
        // Cała lista przycisków – zero marginesów i bardzo mały gap
        '& .MuiPagination-ul': {
		  flexWrap: 'nowrap !important',
		  gap: '4px',
		  padding: 0,
		  margin: 0,
		  whiteSpace: 'nowrap',
		  '& li': {
			margin: 0,
			flexShrink: 0,
		  },
		},

        // Każdy przycisk (liczba, strzałka, ellipsis)
        '& .MuiPaginationItem-root': {
          margin: 0,
          padding: 0,
          minWidth: '20px !important',
          width: '20px !important',
          height: '20px !important',
          fontSize: '0.8125rem', // 13px
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Zabijamy wewnętrzny padding, który MUI dodaje w .MuiButtonBase-root
          '& .MuiButtonBase-root': {
            padding: 0,
            minWidth: '20px',
            width: '20px',
            height: '20px',
          },
        },

        // Strzałki First/Previous/Next/Last
        '& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext': {
          minWidth: '20px !important',
          width: '20px !important',
          '& svg': {
            fontSize: '1.25rem',
          },
        },

        // Elipsa (...)
        '& .MuiPaginationItem-ellipsis': {
          padding: 0,
          width: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    />
  </Box>
)}

      {totalPages > 1 && (
        <Typography variant="body2" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
          Strona {currentPage} z {totalPages}
        </Typography>
      )}

      {/* Tabela */}
      {teamsData.length === 0 ? (
        <Typography variant="h5" gutterBottom align="center">
          Brak drużyn spełniających wybrane kryteria.
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: 'fit-content',
            margin: '0 auto',
            overflowX: 'auto',
            borderRadius: '12px',
          }}
        >
          <Table sx={{ minWidth: 320 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ p: { xs: '4px 6px', sm: '6px 8px' }, fontSize: { xs: '0.75rem', sm: '0.85rem' }, fontWeight: 'bold' }}>
                  #
                </TableCell>
                <TableCell
                  sx={{
                    p: { xs: '4px 6px', sm: '6px 8px' },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    fontWeight: 'bold',
                    width: '45%',
                  }}
                >
                  Drużyna
                </TableCell>
                <TableCell sx={{ p: { xs: '4px 6px', sm: '6px 8px' }, fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 'bold' }}>
                  Liga
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ p: { xs: '4px 6px', sm: '6px 8px' }, fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 'bold' }}
                >
                  ELO
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamsData.map((team, index) => {
                const globalRank = (currentPage - 1) * PAGE_SIZE + index + 1;
                return (
                  <TableRow
                    key={team.teamId}
                    hover
                    sx={{
                      backgroundColor: index % 2 === 1 ? 'rgba(255, 255, 255, 0.04)' : 'transparent',
                    }}
                  >
                    <TableCell sx={{ p: { xs: '4px 6px', sm: '6px 8px' }, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      {globalRank}
                    </TableCell>

                    {/* Klikalna nazwa drużyny */}
                    <TableCell
                      sx={{
                        p: { xs: '4px 6px', sm: '6px 8px' },
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => goToTeam(team.teamId)}
                    >
                      {team.teamName}
                    </TableCell>

                    {/* Klikalna nazwa ligi */}
                    <TableCell
                      sx={{
                        p: { xs: '4px 6px', sm: '6px 8px' },
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        textDecoration: team.competitionId ? 'none' : 'none',
                        cursor: team.competitionId ? 'pointer' : 'default',
                        color: team.competitionId ? 'primary.main' : 'inherit',                      
                      }}
                      onClick={() => goToLeague(team.competitionId)}
                    >
                      {team.competitionName || '–'}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        p: { xs: '4px 6px', sm: '6px 8px' },
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        fontWeight: 'bold',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        transition: 'all 0.2s',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
                      }}
                    >
                      {team.eloRanking}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginacja na dole */}
      {totalPages > 1 && (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      my: 2,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }}
  >
    <Pagination
      count={totalPages}
      page={currentPage}
      onChange={handlePageChange}
      size="medium"
      siblingCount={1}
      boundaryCount={1}
      showFirstButton
      showLastButton
      color="primary"
      sx={{
        // Cała lista przycisków – zero marginesów i bardzo mały gap
        '& .MuiPagination-ul': {
		  flexWrap: 'nowrap !important',
		  gap: '4px',
		  padding: 0,
		  margin: 0,
		  whiteSpace: 'nowrap',
		  '& li': {
			margin: 0,
			flexShrink: 0,
		  },
		},

        // Każdy przycisk (liczba, strzałka, ellipsis)
        '& .MuiPaginationItem-root': {
          margin: 0,
          padding: 0,
          minWidth: '20px !important',
          width: '20px !important',
          height: '20px !important',
          fontSize: '0.8125rem', // 13px
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Zabijamy wewnętrzny padding, który MUI dodaje w .MuiButtonBase-root
          '& .MuiButtonBase-root': {
            padding: 0,
            minWidth: '20px',
            width: '20px',
            height: '20px',
          },
        },

        // Strzałki First/Previous/Next/Last
        '& .MuiPaginationItem-firstLast, & .MuiPaginationItem-previousNext': {
          minWidth: '20px !important',
          width: '20px !important',
          '& svg': {
            fontSize: '1.25rem',
          },
        },

        // Elipsa (...)
        '& .MuiPaginationItem-ellipsis': {
          padding: 0,
          width: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    />
  </Box>
)}
    </Box>
  );
};

export default EloRanking;